import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { finalize, retry, throwError, timer } from 'rxjs';

import { ToastService } from '../services/toast.service';

/**
 * FAST-PIN re-review (item 3) — the hub's `503 pin_check_pending`.
 *
 * Offline, the store hub can sign a cashier in on the first PIN match and
 * check, in the background, that no other user holds the same PIN. Until
 * that check ends the session is provisional: the hub holds each write for
 * up to 20 s, then answers `503 { errorCode: 'pin_check_pending' }`. The
 * write has NOT been applied (the hub answers before running it), so it is
 * safe to send again.
 *
 * This retries such a write on its own behind a "Confirming PIN…" notice,
 * for at most {@link PIN_CHECK_RETRY_TOTAL_MS} from the first refusal, then
 * lets the last refusal through to the caller as before. Nothing else is
 * retried here.
 */
export const PIN_CHECK_RETRY_TOTAL_MS = 90_000;
export const PIN_CHECK_RETRY_DELAY_MS = 1_000;

export function isPinCheckPending(err: unknown): boolean {
  return (
    err instanceof HttpErrorResponse &&
    err.status === 503 &&
    (err.error as { errorCode?: unknown } | null)?.errorCode === 'pin_check_pending'
  );
}

export const pinCheckPendingInterceptor: HttpInterceptorFn = (req, next) => {
  const toast = inject(ToastService);
  let firstRefusalAt: number | null = null;
  let notice: number | null = null;
  const clearNotice = () => {
    if (notice !== null) {
      toast.dismiss(notice);
      notice = null;
    }
  };
  return next(req).pipe(
    retry({
      delay: (err: unknown) => {
        if (!isPinCheckPending(err)) return throwError(() => err);
        const now = Date.now();
        firstRefusalAt ??= now;
        if (now - firstRefusalAt >= PIN_CHECK_RETRY_TOTAL_MS) return throwError(() => err);
        // Stays up (duration 0) until the write goes through or gives up.
        if (notice === null) notice = toast.info('pos.offline.confirmingPin', 0);
        return timer(PIN_CHECK_RETRY_DELAY_MS);
      },
    }),
    finalize(clearNotice),
  );
};
